import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import { toast } from 'sonner';
import { ArrowLeft, ArrowUpDown, Inbox } from 'lucide-react';
import { format } from 'date-fns';

const CATEGORIES = ['Article', 'Tool', 'Simulation'];

const STATUS_CONFIG = {
  NEW: { label: 'New', className: 'bg-blue-100 text-blue-800' },
  UNDER_REVIEW: { label: 'Under Review', className: 'bg-amber-100 text-amber-800' },
  PLANNED: { label: 'Planned', className: 'bg-emerald-100 text-emerald-800' },
  REJECTED: { label: 'Rejected', className: 'bg-red-100 text-red-800' },
};

const AdminTopicRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortDir, setSortDir] = useState('desc');
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const data = await adminApi.getTopicRequests();
        setRequests(data);
      } catch (err) {
        console.error('Error fetching topic requests:', err);
        toast.error('Failed to load topic requests');
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
  }, []);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    setUpdatingId(requestId);
    try {
      await adminApi.updateTopicRequestStatus(requestId, newStatus);
      setRequests((prev) =>
        prev.map((r) => (r.request_id === requestId ? { ...r, status: newStatus } : r))
      );
      toast.success('Status updated');
    } catch (err) {
      console.error('Error updating topic request status:', err);
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const visibleRequests = useMemo(() => {
    let list = requests;
    if (categoryFilter !== 'all') {
      list = list.filter((r) => r.category === categoryFilter);
    }
    if (statusFilter !== 'all') {
      list = list.filter((r) => r.status === statusFilter);
    }
    return [...list].sort((a, b) => {
      const diff = new Date(a.created_at) - new Date(b.created_at);
      return sortDir === 'asc' ? diff : -diff;
    });
  }, [requests, categoryFilter, statusFilter, sortDir]);

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8 px-4" data-testid="admin-topic-requests-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon" data-testid="back-to-admin-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="font-chivo text-2xl font-black uppercase tracking-tight">Topic Requests</h1>
            <p className="text-muted-foreground text-sm">Requests submitted by users for new Articles, Tools, or Simulations</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[160px]" data-testid="filter-category-select">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="filter-status-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([value, { label }]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            className="gap-2"
            data-testid="toggle-sort-date-btn"
          >
            <ArrowUpDown className="h-3.5 w-3.5" />
            Date {sortDir === 'asc' ? '(Oldest First)' : '(Newest First)'}
          </Button>

          <span className="text-sm text-muted-foreground ml-auto">
            {visibleRequests.length} request{visibleRequests.length !== 1 ? 's' : ''}
          </span>
        </div>

        <Card className="border-2">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : visibleRequests.length === 0 ? (
              <div className="py-16 text-center" data-testid="no-topic-requests-message">
                <Inbox className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No topic requests match these filters.</p>
              </div>
            ) : (
              <Table data-testid="topic-requests-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="min-w-[220px]">Description</TableHead>
                    <TableHead>Submitted By</TableHead>
                    <TableHead>Date Submitted</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRequests.map((r) => (
                    <TableRow key={r.request_id} data-testid={`topic-request-row-${r.request_id}`}>
                      <TableCell className="font-medium max-w-[220px] truncate">{r.title}</TableCell>
                      <TableCell>
                        <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                          {r.category}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <span className="text-sm text-muted-foreground line-clamp-2">{r.description}</span>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{r.user_name}</div>
                        <div className="text-xs text-muted-foreground">{r.user_email}</div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {formatDate(r.created_at)}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={r.status}
                          onValueChange={(value) => handleStatusChange(r.request_id, value)}
                          disabled={updatingId === r.request_id}
                        >
                          <SelectTrigger
                            className={`w-[150px] h-8 text-xs border-0 font-semibold ${STATUS_CONFIG[r.status]?.className || ''}`}
                            data-testid={`status-select-${r.request_id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_CONFIG).map(([value, { label }]) => (
                              <SelectItem key={value} value={value}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminTopicRequestsPage;
